import { useEffect, useCallback } from "react";
import { usePublicPlatformSettings } from "./usePublicPlatformSettings";

/**
 * Deferred favicon update - runs during idle time to avoid blocking initial render.
 * PERFORMANCE: Uses requestIdleCallback to prevent blocking FCP/LCP.
 */
export function usePlatformFavicon() {
  const { data: settings } = usePublicPlatformSettings();
  
  const updateFavicons = useCallback((faviconUrl: string) => {
    // Update all favicon links
    const iconLinks = document.querySelectorAll('link[rel="icon"]');
    if (iconLinks.length > 0) {
      iconLinks.forEach(link => {
        (link as HTMLLinkElement).href = faviconUrl;
      });
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
    }
    
    // Update apple-touch-icon
    const appleIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
    if (appleIcon) {
      appleIcon.href = faviconUrl;
    } else {
      const link = document.createElement('link');
      link.rel = 'apple-touch-icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
    }
  }, []);
  
  useEffect(() => {
    // Priority: favicon > mark logo
    const faviconUrl = settings?.platform_favicon_url || settings?.platform_mark_logo_url;
    
    if (faviconUrl && faviconUrl !== 'null') {
      // Defer favicon update to idle time - not critical for initial render
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => updateFavicons(faviconUrl), { timeout: 2000 });
      } else {
        // Fallback for Safari - use setTimeout with 0 delay
        setTimeout(() => updateFavicons(faviconUrl), 0);
      }
    }
  }, [settings, updateFavicons]);
}
